
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbTypeaheadModule } from '@ng-bootstrap/ng-bootstrap';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { TranslateModule } from '@ngx-translate/core';
import { catchError, debounceTime, distinctUntilChanged, map, Observable, of, Subscription, switchMap, tap } from 'rxjs';

import { SasCheckboxComponent } from '../../sas-checkbox/sas-checkbox.component';
import { SasDynamicFormService } from '../sas-dynamic-form.service';
import { FormSubmittedService, SasGazetteerService } from '@services';

const MAX_LENGTH_POSTCODE = 8;
const MIN_STREET_LENGTH = 4;
const MAX_STREET_LENGTH = 100;
const MAX_POSTCODES_DEFAULT = 50;

@Component({
    selector: 'sas-gazetteer-street-field-type',
    standalone: true,
    imports: [ReactiveFormsModule, FormlyModule, TranslateModule, NgbTypeaheadModule, FormsModule, SasCheckboxComponent],
    providers: [],
    template: `
        <h5 translate="GAZETTEER.STREET_TITLE"></h5>
        <div class="row">
            <div class="col-md-6 col-12">
                <div class="mb-3 gazetteer-postcode">
                    <label for="gzPostcode" class="form-label" translate="GAZETTEER.POSTCODE.LABEL"></label>
                    <div class="sas-inline-block" [title]="helpText">
                        <i class="bi bi-info-circle-fill sas-info-icon" aria-hidden="true"></i>
                    </div>
                    <input id="gzPostcode"
                        type="search"
                        name="gzPostcode"
                        class="form-control"
                        [(ngModel)]="postcodeSelected"
                        [attr.placeholder]="'GAZETTEER.POSTCODE.PLACEHOLDER' | translate"
                        [attr.maxlength]="maxlengthPostcode"
                        autocomplete="off"
                        [title]="'GAZETTEER.POSTCODE.LABEL' | translate"
                        aria-invalid="false"
                        aria-label="search postcode"
                        role="combobox"
                        (change)="updateOnChange()"
                        [disabled]="manualAddress || formState.disabled"
                        [ngbTypeahead]="search"
                        (selectItem)="loadAddresses($event.item)"
                        [inputFormatter]="formatter"
                        [resultFormatter]="formatter"
                        [focusFirst]="false"
                        [editable]="true"
                        [showHint]="true"
                        [minLength]="3">
                    @if (loading) {
                    <div class="spinner-border spinner-border-sm" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    }
                </div>
                @if (noResults && (!loading && !loadingStreet)) {
                <i class="bi bi-x-lg"></i> {{ 'GAZETTEER.POSTCODE.NO_RESULTS' | translate }}
                }
            </div>
            <div class="col-md-6 col-12">
                <div class="mb-3 gazetteer-street">
                    <label for="gzStreet" class="form-label" translate="GAZETTEER.STREET.LABEL"></label>
                    <input id="gzStreet"
                        type="search"
                        name="gzStreet"
                        class="form-control"
                        [(ngModel)]="streetSelected"
                        [attr.placeholder]="'GAZETTEER.STREET.PLACEHOLDER' | translate"
                        [attr.maxlength]="maxlengthStreet"
                        [attr.minlength]="minlengthStreet"
                        autocomplete="off"
                        [title]="'GAZETTEER.STREET.LABEL' | translate"
                        aria-invalid="false"
                        aria-label="search street"
                        [disabled]="manualAddress || formState.disabled"
                        (ngModelChange)="takeStreet(streetSelected, $event)">
                    @if (loadingStreet) {
                    <div class="spinner-border spinner-border-sm" role="status">
                        <span class="visually-hidden">Loading...</span>
                    </div>
                    }
                </div>
            </div>
        </div>
        <div class="mb-3">
            <label [id]="field.id + 'Label'" for="address" class="form-label" translate="GAZETTEER.ADDRESS.LABEL"></label>
            @if (field.props.required) {
            <span> *</span>
            }
            <select id="address"
                    name="addressName"
                    class="form-control form-select"
                    [attr.aria-label]="'GAZETTEER.ADDRESS.LABEL' | translate"
                    [(ngModel)]="addressSelected"
                    required="{{field.props.required}}"
                    [disabled]="!addresses || addresses.length === 0 || noResults"
                    (change)="selectAddressAction()"
                    [attr.aria-labelledby]="field.id + 'Label'">
                <option [ngValue]="null">{{'GAZETTEER.ADDRESS.DEFAULT_OPTION' | translate}}</option>
                @for (elem of addresses; track $index) {
                <option [ngValue]="elem">{{elem.displayText}}</option>
                }
            </select>
            <div [id]="field.id + 'Desc'" role="alert" aria-live="assertive" aria-atomic="true" class="invalid-feedback">
                <small>{{ sasDynamicFormService.getFieldErrorMessage(field) }}</small>
            </div>
        </div>
        <sas-checkbox [name]="'manualAddress'"
                      [label]="'GAZETTEER.MANUAL_ADDRESS.LABEL'"
                      [(model)]="manualAddress"
                      (sasOnChange)="setManualAddressMode($event)"
                      [sasDisabled]="formState.disabled">
        </sas-checkbox>
    `,
    styleUrl: './sas-gazetteer-street-field-type.component.scss'
})
export class SasGazetteerStreetFieldTypeComponent extends FieldType<FieldTypeConfig> {
    formSubmittedService = inject(FormSubmittedService);
    private gazetteerService = inject(SasGazetteerService);
    private cdr = inject(ChangeDetectorRef);
    sasDynamicFormService = inject(SasDynamicFormService);

    instanceId = Math.random().toString(36).substring(2);
    postcodeSelected: string = '';
    streetSelected: string = '';
    addressSelected: any = null;
    maxlengthPostcode = MAX_LENGTH_POSTCODE;
    maxlengthStreet = MAX_STREET_LENGTH;
    minlengthStreet = MIN_STREET_LENGTH;
    manualAddress = false;
    addresses: Array<any> = []; //[{ displayText: '123 Main St' }, { displayText: '456 Elm St' }];
    loading: boolean = false;
    loadingStreet: boolean = false;
    noResults: boolean = false;
    maxPostcodesLimit: number = MAX_POSTCODES_DEFAULT;
    streetTimeout: undefined | ReturnType<typeof setTimeout> = undefined
    helpText?: string;

    private valueChangesSubscription!: Subscription;

    ngOnInit() {
        this.helpText = 'Format example for UK: SW1A 1AA\nFormat example for Ireland: A65 F4E2'

        this.manualAddress = this.formControl.value ?? false;
        this.setManualAddressMode();

        this.valueChangesSubscription = this.formControl.valueChanges.subscribe((value) => {
            this.manualAddress = !value;
        });

        const maxPostcodes = this.field.props['maxPostcodes'];
        this.maxPostcodesLimit = maxPostcodes !== undefined && maxPostcodes !== '' && maxPostcodes > 0
            ? maxPostcodes
            : MAX_POSTCODES_DEFAULT;
    }

    search = (text$: Observable<string>) =>
        text$.pipe(
            debounceTime(500),
            distinctUntilChanged(),
            tap(() => {
                this.loading = true;
                this.cdr.detectChanges();
            }),
            switchMap(term => {
                if (term.length < 3) {
                    this.noResults = false;
                }

                return term.length < 3 ? of([]) : this.getPostcodes(term);
            }),
            tap(() => {
                this.loading = false;
                this.cdr.detectChanges();
            })
        );

    formatter = (result: string) => result;

    getPostcodes(term: string): Observable<Array<string>> {
        return this.gazetteerService.retrievePostcodes(term).pipe(
            tap(result => {
                this.noResults = result.length === 0;

                if (this.noResults) this.addresses = [];

                this.cdr.detectChanges();
            }),
            map(result => result.map(item => item.postcode).splice(0, this.maxPostcodesLimit)),
            catchError(() => {
                this.noResults = true;
                this.cdr.detectChanges();

                return of([]);
            })
        );
    }

    takeStreet(streetToken: string, event: Event) {
        if (streetToken.length > MIN_STREET_LENGTH) {
            clearTimeout(this.streetTimeout);

            this.streetTimeout = setTimeout(() => {
                this.postcodeSelected = '';
                this.noResults = false;
                this.loadAddresses(streetToken, true);
            }, 500);
        }
    }

    loadAddresses(token: string, isStreet: boolean = false) {
        if (!isStreet) {
            this.streetSelected = '';
        }
        this.getAddresses(token, isStreet);
    }

    getAddresses(token: string, isStreet: boolean = false) {
        let params: any = { postcode: token };

        if (isStreet) {
            this.loadingStreet = true;
            params = { streetName: token };

            this.cdr.detectChanges();
        }

        this.gazetteerService.retrieveAddresses(params).pipe(
            switchMap(result => {
                if (isStreet) this.loadingStreet = false;
                if (result.length === 0) this.noResults = true;

                this.addresses = this.getAddressesSorted(result);
                this.cdr.detectChanges();

                return this.addresses;
            })
        ).subscribe();
    }

    getAddressesSorted(addresses: Array<any>): Array<any> {
        return addresses.sort((a, b) => a.displayText.localeCompare(b.displayText, 'en', { numeric: true }));
    }

    selectAddressAction() {
        const sourceId = this.field.key ? String(this.field.key) : null;

        this.gazetteerService.setAddressSelected(this.addressSelected, sourceId);  // signal got updated by this set
    }

    updateOnChange() {
        this.noResults = this.noResults && this.postcodeSelected.length > 2;
    }

    setManualAddressMode(manualAddress?: any) {
        this.postcodeSelected = '';
        this.streetSelected = '';
        this.addressSelected = null;
        this.formControl.setValue(!this.manualAddress);
        this.selectAddressAction();
        this.addresses = [];

        // TO review and improve: the event can be received by several gazetteer form elements instances
        // at the same time when the objetive would by just the one, the one received the click on manual address
        // Also, the contant is in another place outside of the component or at least dynamic form component, which break the unity/self-working of the component
        // posible solution is set a new as the standard version for this compnent, sasOnCheck output param.
        // So just the caller would received the event back throw the output param function.

        if (manualAddress) {
            //this.eventService.emitEvent(SharedConstants.GAZETTEER_MANUAL_ADDRESS, { manualAddress: manualAddress.value }); // TO BE DELETED once the calls has been updated
            this.gazetteerService.emitManualAddress(manualAddress.value)
        }
    }

    ngOnDestroy() {
        this.valueChangesSubscription?.unsubscribe();
    }
}
